import { algoliasearch } from "algoliasearch";
import type { SearchClient, SearchResponse } from "algoliasearch";
import { createNullCache } from "@algolia/client-common";
import axiosInstance from "@/api/axiosInstance";
import { FilesystemNode } from "../api/listFolderContents/types";

// Feature flag - can be controlled via env var or settings
export const ENABLE_ALGOLIA_FILES =
  import.meta.env.VITE_ENABLE_ALGOLIA_FILES === "true";

export interface FileSearchConfig {
  securedApiKey: string;
  indices: {
    files: string;
  };
  applicationId: string;
  expiresAt: number;
}

export interface FileSearchFilters {
  parentUuid?: string | null;
  folderUuid?: string | null; // For recursive search — filters by ancestorUuids
  fileTypes?: string[];
  tags?: string[]; // Changed to string[] for tag names from Algolia facets
  recursive?: boolean;
}

export type FileSearchFacets = Record<string, Record<string, number>>;

export interface FileSearchParams {
  query?: string;
  filters?: FileSearchFilters;
  page?: number;
  limit?: number;
  sortBy?: "name" | "updatedAt" | "fileSize" | "fileType";
  sortOrder?: "ASC" | "DESC";
}

export interface FileSearchResult {
  items: FilesystemNode[];
  total: number;
  hasMore: boolean;
  nextCursor?: string;
  facets?: FileSearchFacets;
}

class FileSearchService {
  private searchClient: SearchClient | null = null;
  private config: FileSearchConfig | null = null;
  private configPromise: Promise<FileSearchConfig> | null = null;

  /**
   * Check if Algolia is enabled for files
   */
  isEnabled(): boolean {
    return ENABLE_ALGOLIA_FILES;
  }

  /**
   * Initialize the search service with secured configuration from the API
   */
  async initialize(forceRefresh: boolean = false): Promise<void> {
    if (forceRefresh || !this.configPromise) {
      this.configPromise = this.fetchConfig();
    }

    this.config = await this.configPromise;

    // Initialize Algolia client with secured key.
    // responsesCache: createNullCache() disables the v5 client's in-memory
    // response cache. Without this, navigating back to a folder you
    // previously visited returns the cached response — so a folder created
    // since that visit is invisible until you refresh the page.
    this.searchClient = algoliasearch(
      this.config.applicationId,
      this.config.securedApiKey,
      {
        responsesCache: createNullCache(),
      },
    );
  }

  /**
   * Fetch the search configuration from the API
   */
  private async fetchConfig(): Promise<FileSearchConfig> {
    const response = await axiosInstance.get<FileSearchConfig>(
      "/api/private/file-management/search/config",
    );
    return response.data;
  }

  /**
   * Ensure the client is initialized before making requests
   */
  private async ensureInitialized(): Promise<void> {
    if (!this.searchClient || !this.config) {
      await this.initialize();
    }

    // Check if key is expired and refresh if needed
    if (this.config && this.config.expiresAt < Date.now() / 1000) {
      await this.initialize(true);
    }
  }

  /**
   * Browse folder contents using Algolia
   */
  async browseFolder(params: FileSearchParams): Promise<FileSearchResult> {
    await this.ensureInitialized();

    if (!this.searchClient || !this.config) {
      throw new Error("Search client not initialized");
    }

    const {
      query = "",
      filters = {},
      page = 0,
      limit = 50,
      sortBy = "name",
      sortOrder = "ASC",
    } = params;

    // Build Algolia filters
    const algoliaFilters: string[] = [];

    // Filter by folder scope
    if ("folderUuid" in filters && filters.recursive) {
      // Recursive mode: use ancestorUuids to get all descendants
      if (filters.folderUuid === null) {
        // At root with recursive — no folder filter needed, show everything
      } else if (filters.folderUuid) {
        algoliaFilters.push(`ancestorUuids:"${filters.folderUuid}"`);
      }
    } else if ("parentUuid" in filters) {
      // Non-recursive: direct children only
      if (filters.parentUuid === null) {
        algoliaFilters.push("parentUuid:NULL");
      } else if (filters.parentUuid) {
        algoliaFilters.push(`parentUuid:"${filters.parentUuid}"`);
      }
    }

    // Filter by file types
    if (filters.fileTypes && filters.fileTypes.length > 0) {
      const fileTypeFilters = filters.fileTypes
        .map((type) => `fileType:"${type}"`)
        .join(" OR ");
      algoliaFilters.push(`(${fileTypeFilters})`);
    }

    // Filter by tags (using tag names now)
    if (filters.tags && filters.tags.length > 0) {
      const tagFilters = filters.tags
        .map((tagName) => `tags.name:"${tagName}"`)
        .join(" OR ");
      algoliaFilters.push(`(${tagFilters})`);
    }

    // Determine sort index replica (if configured) or use default with runtime sorting
    // For now, we'll use runtime sorting since we haven't set up index replicas
    const sortByField = this.getSortField(sortBy);

    const finalFilters =
      algoliaFilters.length > 0 ? algoliaFilters.join(" AND ") : "";

    console.log("[Algolia Service] Final filters string:", finalFilters);
    console.log("[Algolia Service] Query:", query);
    console.log("[Algolia Service] Index:", this.config?.indices?.files);

    // An all-digits query is probably a "file library ID" lookup (#9243):
    // run an exact-id search alongside the normal text search (Algolia
    // can't OR a numeric filter into a text query) and surface the exact
    // match first. Unscoped by folder on purpose — an ID lookup is global.
    const idQuery = /^\d+$/.test(query.trim()) ? query.trim() : null;

    try {
      const requests = [
        {
          indexName: this.config.indices.files,
          query,
          filters: finalFilters,
          facets: ["fileType", "tags.name", "type"],
          hitsPerPage: limit,
          page,
          // Custom ranking will handle folder/file ordering
          // Additional sorting can be done client-side if needed
        },
        ...(idQuery !== null
          ? [
              {
                indexName: this.config.indices.files,
                query: "",
                filters: `id=${idQuery}`,
                hitsPerPage: 1,
                page: 0,
              },
            ]
          : []),
      ];
      const searchResponse = await this.searchClient.search<FilesystemNode>({
        requests,
      });

      const result = searchResponse
        .results[0] as SearchResponse<FilesystemNode>;
      const idResult =
        idQuery !== null
          ? (searchResponse.results[1] as SearchResponse<FilesystemNode>)
          : null;

      console.log("[Algolia Service] Raw search response:", result);
      console.log(
        "[Algolia Service] Number of hits:",
        result.hits?.length || 0,
      );
      console.log("[Algolia Service] Total results (nbHits):", result.nbHits);
      console.log("[Algolia Service] First hit (if any):", result.hits?.[0]);
      console.log("[Algolia Service] All hits:", result.hits);

      // Transform Algolia hits to FilesystemNode array
      // Algolia wraps results in Hit<T> which includes objectID and other metadata
      const idHits = idResult?.hits ?? [];
      const combinedHits = [
        ...idHits,
        ...(result.hits || []).filter(
          (hit) => !idHits.some((idHit) => idHit.uuid === hit.uuid),
        ),
      ];
      let items: FilesystemNode[] = combinedHits.map((hit) => ({
        ...hit,
        id: hit.id ?? null,
        // Ensure we have all required fields (Algolia adds objectID which we don't need in our type)
        uuid: hit.uuid,
        name: hit.name,
        fileType: hit.fileType,
        type: hit.type,
        parentUuid: hit.parentUuid === "NULL" ? null : hit.parentUuid, // Convert string 'NULL' back to null
        createdAt: hit.createdAt,
        updatedAt: hit.updatedAt,
        tags: hit.tags || [],
        contentType: hit.contentType,
        fileSize: hit.fileSize,
        url: hit.url,
        path: hit.path,
        thumbnailUrl: hit.thumbnailUrl,
        hasThumbnail: hit.hasThumbnail,
        isPublic: hit.isPublic,
        publicUrl: hit.publicUrl,
      }));

      // Apply client-side sorting if needed (Algolia already does folder-first sorting)
      if (sortBy !== "name" || sortOrder !== "ASC") {
        items = this.sortItems(items, sortBy, sortOrder);
      }

      return {
        items,
        total: result.nbHits || 0,
        hasMore: (result.page || 0) < (result.nbPages || 1) - 1,
        nextCursor: undefined, // Algolia uses page-based pagination
        facets: result.facets as FileSearchFacets,
      };
    } catch (error) {
      console.error("Algolia search failed:", error);
      throw error;
    }
  }

  /**
   * Search files with query
   */
  async searchFiles(params: FileSearchParams): Promise<FileSearchResult> {
    // Same as browseFolder but with emphasis on search query
    return this.browseFolder(params);
  }

  /**
   * Get search suggestions for autocomplete
   */
  async getSuggestions(query: string, limit: number = 5): Promise<string[]> {
    await this.ensureInitialized();

    if (!this.searchClient || !this.config) {
      throw new Error("Search client not initialized");
    }

    try {
      const searchResponse = await this.searchClient.search<FilesystemNode>({
        requests: [
          {
            indexName: this.config.indices.files,
            query,
            hitsPerPage: limit,
            attributesToRetrieve: ["name"],
          },
        ],
      });

      const result = searchResponse
        .results[0] as SearchResponse<FilesystemNode>;
      // Extract name from each hit (Algolia wraps in Hit<T> type)
      return (result.hits || []).map((hit) => (hit as any).name || "");
    } catch (error) {
      console.error("Failed to get suggestions:", error);
      return [];
    }
  }

  /**
   * Get facet values for dynamic filters
   */
  async getFacets(filters?: FileSearchFilters): Promise<FileSearchFacets> {
    await this.ensureInitialized();

    if (!this.searchClient || !this.config) {
      throw new Error("Search client not initialized");
    }

    // Build base filters (excluding the facet we're getting values for)
    const algoliaFilters: string[] = [];

    if (filters?.parentUuid !== undefined) {
      if (filters.parentUuid === null) {
        algoliaFilters.push("parentUuid:NULL");
      } else {
        algoliaFilters.push(`parentUuid:"${filters.parentUuid}"`);
      }
    }

    try {
      const searchResponse = await this.searchClient.search<FilesystemNode>({
        requests: [
          {
            indexName: this.config.indices.files,
            query: "",
            filters: algoliaFilters.join(" AND "),
            facets: ["fileType", "tags.name", "type"],
            hitsPerPage: 0, // We only want facets, not hits
          },
        ],
      });

      const result = searchResponse
        .results[0] as SearchResponse<FilesystemNode>;
      return (result.facets as FileSearchFacets) || {};
    } catch (error) {
      console.error("Failed to get facets:", error);
      return {};
    }
  }

  /**
   * Helper to get Algolia field name for sorting
   */
  private getSortField(sortBy: string): string {
    switch (sortBy) {
      case "fileType":
        return "fileType";
      case "updatedAt":
        return "updatedAt";
      case "fileSize":
        return "fileSize";
      case "name":
      default:
        return "name";
    }
  }

  /**
   * Client-side sorting of items (Algolia already handles folder-first sorting)
   */
  private sortItems(
    items: FilesystemNode[],
    sortBy: string,
    sortOrder: "ASC" | "DESC",
  ): FilesystemNode[] {
    const sorted = [...items].sort((a, b) => {
      // Always keep folders first (Algolia should already do this)
      if (a.type !== b.type) {
        return a.type === "folder" ? -1 : 1;
      }

      let compareValue = 0;
      switch (sortBy) {
        case "name":
          compareValue = a.name.localeCompare(b.name);
          break;
        case "fileType":
          compareValue = (a.fileType || "").localeCompare(b.fileType || "");
          break;
        case "updatedAt":
          compareValue =
            new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
          break;
        case "fileSize":
          compareValue = (a.fileSize || 0) - (b.fileSize || 0);
          break;
        default:
          compareValue = a.name.localeCompare(b.name);
      }

      return sortOrder === "ASC" ? compareValue : -compareValue;
    });

    return sorted;
  }

  /**
   * Clear the cached configuration
   */
  clearCache(): void {
    this.searchClient = null;
    this.config = null;
    this.configPromise = null;
  }
}

// Export singleton instance
export const fileSearchService = new FileSearchService();
